import type { SupportCase } from "@tdelivr/api-client/generated/support/support-case.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { at, field, type RecordDetail } from "./record-detail.ts";

/**
 * What the case queue does to the cases SUP-01 returns, away from any rendering.
 *
 * Every column the design's grid draws has a published source, and the two things on the page that
 * do not are both recorded below rather than filled in.
 *
 * **The state vocabulary is the contract's, mapped onto the design's nine classes by hand.**
 * `@tdelivr/design-tokens` generates its status-to-class table from `01 Token System v2.dc.html`,
 * and that file's table has no entry for SUP-01 — so `classOfStatus("SUP-01", …)` answers
 * `undefined` for all five statuses and every badge would render unclassified. `stateOf` supplies
 * the class, which is what `StateBadge`'s `classKey` prop exists for, and the mapping is derived
 * from the design's own badge words on this page rather than chosen.
 *
 * **There is no reply deadline anywhere on the platform, and this page is built around one.** The
 * design's own title says "sorted by urgency and **time to breach**", its tiles read
 * `Breaching in 30 min · 3 · SLA` and `Breached · 1 · reply overdue`, and its rows read
 * `reply due 12 min` and `breached 6 min`. Nothing on any of the 142 contracts publishes a `dueAt`,
 * a `slaBreachesAt`, a `respondBy` or a remaining duration — not on SUP-01, not on SUP-02, not on
 * SUP-05, which models escalation timers and is entirely `/internal/v1`. A countdown this console
 * computed from `createdAt` and a policy it invented would be a number an agent paces a shift by.
 * So **no deadline is drawn and no tile counting one is drawn**, and the page says so once. R-35.
 *
 * **The customer is a reference, not a name and not a phone number.** The design writes
 * `Amina K. · +254 7•• ••• 421` and the masking is itself a designed behaviour — the page's own tip
 * says "Phone numbers stay masked. Revealing one asks for a reason and is logged." SUP-01 publishes
 * `customerReference`, which is an opaque `customer:<subject>` string. CUS-01 holds display names,
 * and joining to it per row is a second service on one screen — the fan-out Document 17 WEB-0743
 * tolerates for one listing per screen and not for a column. The cell draws the reference in mono
 * and says which it is. R-35.
 */

/**
 * How many cases to take in one request.
 *
 * 200 is SUP-01's own maximum: `limit: int, min 1, max 200`, default 50.
 *
 * **There is no cursor.** The listing publishes `supportCases` and nothing else — no `nextAfter`,
 * no `nextCursor` — so a board cannot ask for older cases and this number is the whole of what it
 * can hold. That is stated under the grid rather than hidden behind a Next button that would do
 * nothing, and it is the one absence on this screen that is route work rather than a missing
 * domain: the repository query is already bounded and already ordered.
 */
export const PER_FETCH = 200;

/** SUP-01's published status enum, as one name. */
export type CaseStatus = SupportCase["status"];

/** SUP-01's published priority enum, as one name. */
export type CasePriority = SupportCase["priority"];

/**
 * The state class and the word, from `status`.
 *
 * | status             | class     | word                 | where the word comes from                      |
 * | ------------------ | --------- | -------------------- | ---------------------------------------------- |
 * | `open`             | `active`  | `open`               | the design's `Open · 48 · active` tile         |
 * | `pending_support`  | `human`   | `needs a person`     | the design's `human · needs a supervisor` row  |
 * | `pending_customer` | `waiting` | `waiting on customer`| the design's `Waiting on customer · 12` tile   |
 * | `resolved`         | `done`    | `resolved`           | the contract's own word                        |
 * | `closed`           | `closed`  | `closed`             | the contract's own word                        |
 *
 * Two decided here rather than copied, each with the reason.
 *
 * **`pending_support` is `human` and not `active`.** The design's violet `human` class is for a row
 * that is waiting on a person rather than on a system, and `pending_support` is exactly that: the
 * case is with support and the customer has done their part. The design's own `human` row reads
 * `needs a supervisor`, which is a narrower claim than this status makes — nothing publishes
 * *which* person — so the word is the broader one and the status is on the badge's title.
 *
 * **`pending_customer` is `waiting` and not `idle`.** `waiting` is the design's class for a row
 * whose next move belongs to somebody outside the console, which is the whole difference an agent
 * works from: a case waiting on a customer is not a case the agent is late on.
 */
export function stateOf(supportCase: SupportCase): { classKey: StateClassKey; label: string } {
  switch (supportCase.status) {
    case "open":
      return { classKey: "active", label: "open" };
    case "pending_support":
      return { classKey: "human", label: "needs a person" };
    case "pending_customer":
      return { classKey: "waiting", label: "waiting on customer" };
    case "resolved":
      return { classKey: "done", label: "resolved" };
    case "closed":
      return { classKey: "closed", label: "closed" };
  }
}

/**
 * How the design sorts this board: urgency first.
 *
 * The page's own description is "Open cases sorted by urgency and time to breach". The second half
 * has no source — see this file's head — and the first half does: SUP-01 publishes `priority` as a
 * four-value enum. Ordering by it is the design's own intent carried out with the field that
 * exists, rather than a sort this console made up.
 *
 * Lower is more urgent, so the numbers read as rank rather than as weight.
 */
export function urgencyRank(priority: CasePriority): number {
  switch (priority) {
    case "urgent":
      return 0;
    case "high":
      return 1;
    case "normal":
      return 2;
    case "low":
      return 3;
  }
}

/**
 * The design's `Topic` cell: the subject, then the category.
 *
 * The design writes `Payment outcome unknown · co_7Q2K` — a subject and **the record it is about**.
 * SUP-01 publishes `subject` and `category` and nothing that links a case to a trip, an order or a
 * payment: there is no `relatedReference`, no `linkedTripId`, no `subjectRef`. SUP-04's customer-360
 * read model holds case links and is entirely `/internal/v1`, and its listing operation is a
 * projection write rather than a read a screen can make.
 *
 * So the cell is the subject and the category, which is the published pair closest to what the
 * design draws, and the missing half is said under the grid once rather than on every row.
 */
export function topicCell(supportCase: SupportCase): string {
  return `${supportCase.subject} · ${supportCase.category}`;
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type CaseOrder = "default" | "state" | "az" | "za";

export interface CaseQuery {
  readonly search: string;
  readonly order: CaseOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
}

export interface CaseView {
  readonly rows: readonly SupportCase[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly SupportCase[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row a search should match: the published values and the words on screen. */
function searchableText(supportCase: SupportCase): string {
  return [
    supportCase.id,
    supportCase.customerReference,
    supportCase.subject,
    supportCase.category,
    supportCase.priority,
    supportCase.status,
    stateOf(supportCase).label,
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is urgency then newest, which is the design's own stated order with its second term
 * replaced by the one that has a source. SUP-01 answers newest first — `created_at DESC` — so the
 * secondary ordering is the service's own and is preserved rather than re-sorted.
 *
 * The state filter is over the rows and **not** sent as a service parameter, which is what the
 * listing's `limit` is the only parameter for anyway. The reason is the same one the finance
 * console states: the design's filter is populated from the states present in what came back.
 */
export function casePage(rows: readonly SupportCase[], query: CaseQuery, state?: string): CaseView {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (supportCase) =>
      (state === undefined || stateOf(supportCase).label === state) &&
      (needle.length === 0 || searchableText(supportCase).includes(needle)),
  );

  const ordered =
    query.order === "default"
      ? // Urgency, with the service's newest-first order kept inside each band. `sort` is stable in
        // every engine this runs on, so an equal rank leaves the rows as SUP-01 answered them.
        [...matched].sort((left, right) => urgencyRank(left.priority) - urgencyRank(right.priority))
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return stateOf(left).label.localeCompare(stateOf(right).label);
          }
          // "Name" is the first column, as the design's A–Z is. Here that is the case.
          const compared = left.id.localeCompare(right.id);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(rows: readonly SupportCase[]): readonly string[] {
  return [...new Set(rows.map((supportCase) => stateOf(supportCase).label))];
}

/**
 * The sentence this board owes an agent about what it actually fetched.
 *
 * Two claims, and the second is the one that distinguishes this board from every other in this
 * repository: SUP-01's listing publishes **no cursor at all**, so there is no "older cases" to
 * fetch and the board's reach is its `limit` and nothing more. A board that said nothing would let
 * an agent read 200 rows as the queue.
 *
 * It also says which cases the caller is being answered, because that is decided by the token
 * rather than by this page: SUP-01 derives the customer reference from the verified subject, so an
 * agent holding `support-cases:agent` is answered the tenant's cases and anybody else only their
 * own — with no parameter by which the difference could be asked for.
 */
export function fetchedNote(fetched: number | undefined, asAgent: boolean): string | undefined {
  if (fetched === undefined) return undefined;
  const scope = asAgent
    ? "These are the tenant's cases, because this account holds support-cases:agent."
    : "These are this account's own cases only. The tenant's queue needs support-cases:agent, which SUP-01 reads from the token and not from this page.";
  return (
    `The count above is the ${String(fetched)} cases this board asked for, not the tenant's total — ` +
    `SUP-01 publishes neither a count nor a cursor, so there are no older cases to fetch and this ` +
    `limit is the whole of what a board can hold. ${scope}`
  );
}

/**
 * The selected case, for the panel beside the board.
 *
 * **The priority is a field here and a sort there.** The grid orders by it and draws no column for
 * it, because the design draws three columns; the panel names it, because "why is this case above
 * that one" is the question a reader of a sorted board has.
 *
 * **The customer reference is drawn as what it is.** `customer:<subject>` is an identifier, and the
 * panel labels it `Customer reference` rather than `Customer` — an identifier under a label meaning
 * name is the one thing the record-detail rule in `record-detail.ts` forbids.
 */
export function caseDetail(supportCase: SupportCase): RecordDetail {
  const state = stateOf(supportCase);

  return {
    headline: supportCase.subject,
    identifier: supportCase.id,
    source: "SUP-01",
    state: { ...state, code: "SUP-01", status: supportCase.status },
    fields: [
      field("Topic", supportCase.category, true),
      field("Priority", supportCase.priority),
      field("Customer reference", supportCase.customerReference, true),
      field("Opened", at(supportCase.createdAt) ?? supportCase.createdAt),
      field("Last changed", at(supportCase.updatedAt) ?? supportCase.updatedAt),
      field("Version", `v${String(supportCase.version)}`),
    ],
    note:
      "SUP-01 publishes no reply deadline and nothing links a case to the trip, order or payment " +
      "it is about. The design draws both. See R-35.",
  };
}
